import React, { useState } from "react";
import { useParams, useLoaderData } from "react-router-dom";
import { FullPagePaperLayout } from "../Layouts/FullPagePaperLayout";
import { PageBreadcrumbs, PageSubtitle, PageTitle } from "../Layouts/components/PageTitleContainer";
import { Stack, Button, Select, MenuItem, Box, TextField } from "@mui/material";
import { LocationTypeMovementCondition } from "types/locationManagement/locationTypeMovementCondition";
import { LocationManagementLocationType } from "types/locationManagement/locationType";
import { FieldControl } from "components/Form/FieldControl";
import { Form } from "components/Form/Form";

const LocationTypeMovementConditionForm: React.FC = () => {
  const { locationTypeMovementConditionId } = useParams<{ locationTypeMovementConditionId?: string }>();
  const isEdit = Boolean(locationTypeMovementConditionId);
  const { condition, locationTypes } = useLoaderData() as {
    condition: LocationTypeMovementCondition;
    locationTypes: LocationManagementLocationType[];
  };
  const [inventoryAttribute, setInventoryAttribute] = useState<string>(condition.inventoryAttribute || "");
  const [conditionType, setConditionType] = useState<string>(condition.conditionType || "");
  const [conditionValue, setConditionValue] = useState<string>(condition.conditionValue || "");

  const inventoryAttributeOptions = [
    { value: "enrolled_status", label: "Enrolled Status" },
    { value: "state", label: "State" },
    { value: "drive_1_erasure_status", label: "Drive 1 Erasure Status" },
    { value: "photos_taken", label: "Photos Taken" },
    { value: "attributes_completed", label: "Attributes completed" },
    { value: "has_tracking_number", label: "Has Tracking Number" },
    { value: "pickup_id", label: "Pickup ID" },
    { value: "sold_price_in_cents", label: "Sold Price in Cents" },
    { value: "manifest_id", label: "Manifest ID" },
    { value: "attribute_complete", label: "Attribute Complete" },
  ];

  const conditionTypeOptions = [
    { value: "required", label: "Required" },
    { value: "equals", label: "Equals" },
    { value: "not_equals", label: "Not Equals" },
    { value: "in_list", label: "In List" },
    { value: "not_in_list", label: "Not In List" },
  ];

  const selectOptions: Record<string, { value: string; label: string }[]> = {
    state: [
      { value: "resellable", label: "Resellable" },
      { value: "in_process", label: "In Process" },
      { value: "ewasted", label: "Ewasted" },
      { value: "deleted", label: "Deleted" },
      { value: "invoiced", label: "Invoiced" },
      { value: "reserved", label: "Reserved" },
      { value: "missing", label: "Missing" },
      { value: "pending_review", label: "Pending Review" },
      { value: "wip", label: "WIP" },
      { value: "audit", label: "Audit" },
      { value: "return", label: "Return" },
      { value: "unfulfilled", label: "Unfulfilled" },
      { value: "shipped", label: "Shipped" },
      { value: "receiving", label: "Receiving" },
      { value: "tech", label: "Tech" },
      { value: "resale", label: "Resale" },
      { value: "deployable", label: "Deployable" },
      { value: "deployed", label: "Deployed" },
      { value: "retired", label: "Retired" },
    ],
  };

  return (
    <FullPagePaperLayout>
      <PageBreadcrumbs
        links={[
          { label: "Admin", path: "/" },
          { label: "Location Type Movement Conditions", path: "/location-type-movement-conditions" },
        ]}
      />
      <PageTitle>{isEdit ? "Edit" : "Create"} Movement Condition</PageTitle>
      <PageSubtitle>
        Define conditions that must be met for inventory to move from one Location Type to another. These conditions
        will be checked before allowing the movement.
      </PageSubtitle>
      <Box sx={{ m: 3 }}>
        <Form method={isEdit ? "PUT" : "POST"} resource="locationTypeMovementCondition">
          <Stack direction="column" spacing={2}>
            <FieldControl
              name="fromLocationTypeId"
              label="From Location Type"
              inputProps={{ defaultValue: condition.fromLocationTypeId || "" }}
              required
            >
              {({ name, label, defaultValue, labelId }) => (
                <Select name={name} labelId={labelId} defaultValue={defaultValue} required label={label}>
                  {locationTypes.map((locationType) => (
                    <MenuItem key={locationType.id} value={locationType.id}>
                      {locationType.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </FieldControl>
            <FieldControl
              name="toLocationTypeId"
              label="To Location Type"
              inputProps={{ defaultValue: condition.toLocationTypeId || "" }}
              required
            >
              {({ name, label, defaultValue, labelId }) => (
                <Select name={name} labelId={labelId} defaultValue={defaultValue} required label={label}>
                  {locationTypes.map((locationType) => (
                    <MenuItem key={locationType.id} value={locationType.id}>
                      {locationType.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </FieldControl>
            <FieldControl
              name="inventoryAttribute"
              label="Inventory Attribute"
              inputProps={{ defaultValue: condition.inventoryAttribute || "" }}
              required
            >
              {({ name, label, defaultValue, labelId }) => (
                <Select
                  name={name}
                  labelId={labelId}
                  defaultValue={defaultValue}
                  required
                  label={label}
                  onChange={(e) => setInventoryAttribute(e.target.value)}
                  value={inventoryAttribute}
                >
                  {inventoryAttributeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </FieldControl>
            <FieldControl
              name="conditionType"
              label="Condition Type"
              inputProps={{ defaultValue: condition.conditionType || "" }}
              required
            >
              {({ name, label, defaultValue, labelId }) => (
                <Select
                  name={name}
                  labelId={labelId}
                  defaultValue={defaultValue}
                  required
                  label={label}
                  onChange={(e) => setConditionType(e.target.value)}
                  value={conditionType}
                >
                  {conditionTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </FieldControl>
            {selectOptions[inventoryAttribute] && (
              <FieldControl
                name="conditionValue"
                label="Condition Value"
                inputProps={{ defaultValue: condition.conditionValue || "" }}
                required
              >
                {({ name, label, defaultValue, labelId }) => (
                  <Select
                    name={name}
                    labelId={labelId}
                    multiple={["in_list", "not_in_list"].includes(conditionType)}
                    defaultValue={
                      ["in_list", "not_in_list"].includes(conditionType) ? defaultValue || [] : defaultValue || ""
                    }
                    required
                    label={label}
                    onChange={(e) => setConditionValue(e.target.value)}
                    value={conditionValue}
                  >
                    {selectOptions[inventoryAttribute].map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </FieldControl>
            )}
            {inventoryAttribute !== "state" && (
              <FieldControl
                name="conditionValue"
                label="Condition Value"
                inputProps={{ defaultValue: condition.conditionValue || "" }}
                component={TextField}
                required
              />
            )}
            <FieldControl
              name="description"
              label="Description"
              inputProps={{ defaultValue: condition.description || "" }}
              component={TextField}
            />
            <div>
              <Button type="submit" variant="contained">
                {isEdit ? "Update" : "Create"}
              </Button>
            </div>
          </Stack>
        </Form>
      </Box>
    </FullPagePaperLayout>
  );
};

export default LocationTypeMovementConditionForm;
